import ReactDOM from 'react-dom/client';
import 'prismjs';

import { redirect, type RouteObject } from 'react-router-dom';

import './index.css';
import './prism.css';

import { documentationArticles } from './articles/docsArticles';
import { endpointArticles } from './articles/endpointArticles';
import type { Article } from './models/articles';
import type { IPage } from './models/pages';

import './axiosSetup';

import { resolvePages } from './utils/resolvePages';

import { App } from './App';
import { NotFound } from './components/NotFound';
import { Page } from './components/Page';
import { Section, type AppSection } from './pages/Section';

import { type OpenAPIV3_1 } from 'openapi-types';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-ruby';
import { parseSwagger } from './utils/findEndpointDefinitionByArticle';
import { SupportPage } from './components/SupportPage';
/**
 * React setup
 */
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const parsedArticles = {
  documentation: documentationArticles as Array<Article>,
  endpoints: endpointArticles as Array<Article>
};

if (!parsedArticles?.documentation || !parsedArticles?.endpoints)
  throw Error('Unable to initialise application. Unable to read static files.');

const documentationPages: Array<IPage> = resolvePages(
  parsedArticles.documentation
);
const endpointPages: Array<IPage> = resolvePages(parsedArticles.endpoints);

async function setupRoutes(): Promise<RouteObject[]> {
  const guideRoutes = await createRoutes(documentationPages, 'guide');
  const referenceRoutes = await createRoutes(endpointPages, 'reference');

  const supportRoutes: RouteObject[] = [
    { path: '/support', element: <SupportPage /> }
  ];

  const routes: Array<RouteObject> = [
    {
      path: '/',
      loader: () => {
        return redirect('/guide/overview');
      }
    },
    {
      id: 'guide',
      path: '/',
      element: <Section />,
      loader: () => {
        return [
          { id: 'guide', pages: documentationPages },
          { id: 'reference', pages: endpointPages }
        ];
      },
      children: [...guideRoutes, ...referenceRoutes, ...supportRoutes]
    },
    {
      path: '*',
      element: <NotFound />
    }
  ];

  return routes;
}

/**
 * adds the swaggerFile to the routeData, so that it's accessible to use inside the route
 */
function enhancePageData(
  page: IPage,
  swaggerFile: OpenAPIV3_1.Document | null
): IPage {
  return {
    ...page,
    swaggerDoc: swaggerFile,
    children: page.children?.map((child) => enhancePageData(child, swaggerFile))
  };
}

/**
 * Generates routes recursively given a n-depth json object
 */
async function createRoutes(
  pages: IPage[],
  parentSection: AppSection
): Promise<RouteObject[]> {
  const routes: RouteObject[] = [];

  if (parentSection === 'support') {
    const element = <SupportPage />;
    routes.push({ path: '/support', element });
  }

  async function traverse(
    pages: IPage[],
    inheritedSwaggerFile?: OpenAPIV3_1.Document | null
  ) {
    for (const page of pages) {
      const path = `/${parentSection}/${page.id}`;
      let swaggerFile = inheritedSwaggerFile;

      if (parentSection === 'guide') {
        const element = <Page routeData={page} />;
        routes.push({ path, element });
      }

      if (parentSection === 'reference' && page.article?.swaggerFilePath) {
        swaggerFile = await parseSwagger(page.article?.swaggerFilePath);
      }

      const element = (
        <Page
          routeData={swaggerFile ? enhancePageData(page, swaggerFile) : page}
        />
      );
      routes.push({ path, element });

      if (page.children) {
        await traverse(page.children, swaggerFile);
      }
    }
  }

  await traverse(pages);

  return routes;
}

setupRoutes().then((routes) => {
  root.render(<App routes={routes} />);
});
